import { api } from './api';

export interface Mapping {
  id: string;
  meeting_space_id: string;
  meeting_space_name: string | null;
  discovered_room_id: string;
  room_calendar_id: string;
  registration_state: 'pending' | 'partial' | 'active' | 'failed';
  provider_subscribed: boolean;
  zencore_subscribed: boolean;
  synced_through: string | null;
  last_error_code: string | null;
}

/**
 * `GET /calendar/pods` — the organization's ZenCore spaces, linked or not.
 *
 * Separate from suggestions on purpose: the pod list used to be whatever the
 * suggestion call happened to return, so an organization with no confident
 * name matches saw an empty screen under a banner saying "link these by hand".
 */
export interface Pod {
  meeting_space_id: string;
  meeting_space_name: string;
  /**
   * The group this pod belongs to. Pod names repeat across groups — an estate
   * can have a "ZenPod 1" in every one — so without this the list reads as
   * duplicated rows with no way to tell which is which.
   */
  space_group_id: string | null;
  space_group_name: string | null;
  capacity: number | null;
  floor: string | null;
  mapping_id: string | null;
  discovered_room_id: string | null;
}

export interface Suggestion {
  meeting_space_id: string;
  meeting_space_name: string | null;
  room_id: string;
  room_name: string | null;
  score: number;
  reason: string;
}

export interface ApplyResult {
  meetingSpaceId: string;
  roomId: string;
  status: 'created' | 'already_mapped' | 'resumed' | 'rejected';
  mappingId: string | null;
  code: string | null;
  message: string | null;
}

export interface BulkResult {
  requested: number;
  created: number;
  already_mapped: number;
  resumed: number;
  rejected: number;
  results: ApplyResult[];
}

/**
 * Batched deliberately. The server serialises each row on purpose — two
 * registrations per pod against rate-limited APIs — so sending 500 in one
 * request would hold a connection open for minutes. Batching also means a
 * network drop costs one batch rather than the lot, and re-running is safe.
 */
export const BULK_BATCH_SIZE = 50;

export const mappingsApi = {
  list: () => api<Mapping[]>('/calendar/mappings'),

  pods: () => api<Pod[]>('/calendar/pods'),

  suggest: (meetingSpaceIds?: string[]) =>
    api<Suggestion[]>('/calendar/mappings/suggest', {
      method: 'POST',
      data: meetingSpaceIds ? { meeting_space_ids: meetingSpaceIds } : {},
    }),

  applyBatch: (mappings: Array<{ meeting_space_id: string; meeting_space_name?: string; room_id: string }>) =>
    api<BulkResult>('/calendar/mappings/bulk', { method: 'POST', data: { mappings } }),

  remove: (id: string) => api<null>(`/calendar/mappings/${id}`, { method: 'DELETE' }),
};
